import { atom, selector } from 'recoil'
import { Colors } from 'src/components/theme'
import { keyFactory, localStorageEffect } from 'src/utils/recoil'
import AlphamapLogo from 'src/assets/images/logo.png'
import { googlePlace } from 'src/atoms/search'
import { getRenderConfig } from 'src/views/insight-download/render-config'
import type { InsightWidget, PageRenderParams, PagesRenderConfig } from 'src/types/report-insights'

import { PAGE_SIZES } from 'src/constants/report-insights'
import { convertStringRGBtoHex } from 'src/utils/colors'
import { hexToRgb } from '@mui/material'
import { loadImage } from 'src/atoms/utils/image-utils'
import { currentUser } from 'src/atoms/user'

export const PDF_SPACING = 40
export const INSIGHT_WIDGET_PRINTABLE_CLASS = 'insight-widget-printable'
export const INSIGHT_WIDGET_LOCKED_CLASS = 'insight-widget-locked'

const key = keyFactory('report-insight')

export const loading = atom<boolean>({
  key: key('loading'),
  default: false,
})

export const widgets = atom<InsightWidget[]>({
  key: key('widgets'),
  default: [],
})

export const lockedWidgets = atom<Partial<InsightWidget>[]>({
  key: key('locked-widgets'),
  default: [],
})

export const visibleImages = selector<Array<NonNullable<InsightWidget['image']>>>({
  key: key('visible-images'),
  get: ({ get }) => {
    const widgetsValue = get(widgets)
    return widgetsValue.filter((widget) => widget.visible).map((widget) => widget.image) as string[]
  },
})

export const selectedWidgetId = atom<string | undefined>({
  key: key('selected-widget-id'),
  default: undefined,
})

export const selectedWidget = selector<InsightWidget | undefined>({
  key: key('selected-widget'),
  get: ({ get }) => {
    const id = get(selectedWidgetId)
    const widgetsValue = get(widgets)
    return widgetsValue.find((widget) => widget.id === id)
  },
})

// Branding
export type ReportTheme = {
  id: string
  name: string
  logo?: string | null
  primaryColor: string
  backgroundColor: string
}

const hexBlueDark = convertStringRGBtoHex(Colors.blueDark).toUpperCase()
const hexBgMedium = convertStringRGBtoHex(Colors.bgMedium).toUpperCase()

export const themes = atom<Record<string, ReportTheme>>({
  key: key('themes'),
  default: {
    alphamap: {
      id: 'alphamap',
      name: 'AlphaMap',
      logo: AlphamapLogo.src,
      primaryColor: hexBlueDark,
      backgroundColor: hexBgMedium,
    },
    custom: {
      id: 'custom',
      name: 'Custom',
      logo: null,
      primaryColor: hexBlueDark,
      backgroundColor: hexBgMedium,
    },
  },
})

export const theme = atom<string>({
  key: key('theme'),
  default: 'alphamap',
  effects: [localStorageEffect('insight-report:default-theme')],
})

export const branding = selector<ReportTheme>({
  key: key('branding'),
  get: ({ get }) => {
    const reportThemes = get(themes)
    const reportTheme = get(theme)
    return reportThemes[reportTheme]
  },
})

// Report Page size
export const reportPageSize = atom<keyof typeof PAGE_SIZES>({
  key: key('page-size'),
  default: 'letter',
})

// Report Page dimensions
export const reportPageDimensions = selector({
  key: key('page-dimensions'),
  get: ({ get }) => {
    const pageSize = get(reportPageSize)
    return PAGE_SIZES[pageSize]
  },
})

// Report Orientation
export const InsightReportOrientations = {
  Portrait: 'portrait',
  Landscape: 'landscape',
} as const
export type InsightReportOrientation = (typeof InsightReportOrientations)[keyof typeof InsightReportOrientations]

export const reportOrientation = atom<InsightReportOrientation>({
  key: key('report-orientation'),
  default: InsightReportOrientations.Portrait,
})

export const reportPageColor = atom<string>({
  key: key('report-page-color'),
  default: Colors.bgAltBlue,
})

export const customReportPageColor = selector({
  key: key('custom-report-page-color'),
  get: ({ get }) => {
    const theme = get(branding)
    const pageColor = get(reportPageColor)
    return hexToRgb(theme.backgroundColor) || pageColor
  },
})

// Report header
export const reportTitle = atom({
  key: key('report-title'),
  default: {
    text: '',
    fontColor: Colors.textBlack,
    fontSize: 16,
  },
})

export const reportSubtitle = selector({
  key: key('report-subtitle'),
  get: ({ get }) => {
    const place = get(googlePlace)
    return {
      text: place ? `${place.name} · ${place.formatted_address}` : '',
      fontColor: Colors.textDark,
      fontSize: 12,
    }
  },
})

const defaultReportHeader = selector({
  key: key('header'),
  get: ({ get }) => {
    const title = get(reportTitle)
    const subtitle = get(reportSubtitle)
    return {
      title,
      subtitle,
      visible: true,
      height: PDF_SPACING,
    }
  },
})

export const reportHeader = atom({
  key: key('report-header'),
  default: defaultReportHeader,
})

// Report footer
export const reportFooter = atom({
  key: key('report-footer'),
  default: {
    logo: AlphamapLogo.src,
    height: PDF_SPACING,
    visible: true,
  },
})

export const customReportFooter = selector({
  key: key('custom-report-footer'),
  get: ({ get }) => {
    const theme = get(branding)
    const footer = get(reportFooter)
    return {
      ...footer,
      logo: theme.logo || AlphamapLogo.src,
      color: hexToRgb(theme.primaryColor),
    }
  },
})

// Report Cover Page
const defaultReportCoverPage = selector({
  key: key('default-report-cover-page'),
  get: ({ get }) => {
    const title = get(reportTitle)
    const subtitle = get(reportSubtitle)
    return {
      title: title.text,
      subtitle: subtitle.text,
      logo: AlphamapLogo.src,
      color: Colors.blueDark,
    }
  },
})
export const reportCoverPage = atom<PageRenderParams['coverPage'] | undefined>({
  key: key('report-cover-page'),
  default: defaultReportCoverPage,
})

export const reportLogoDimensions = selector({
  key: key('report-logo-dimensions'),
  get: async ({ get }) => {
    const theme = get(branding)
    const image = await loadImage(theme.logo || AlphamapLogo.src)
    // fit image to max 25px height + keep aspect ratio
    const logoHeight = 25
    const logoWidth = (image.width / image.height) * logoHeight
    return { width: logoWidth, height: logoHeight }
  },
})

export const customReportCoverPage = selector({
  key: key('custom-report-cover-page'),
  get: ({ get }) => {
    const theme = get(branding)
    const coverPage = get(reportCoverPage)
    if (!coverPage) {
      return undefined
    }
    return {
      ...coverPage,
      logo: theme.logo || AlphamapLogo.src,
      color: hexToRgb(theme.primaryColor),
    } as PageRenderParams['coverPage'] | undefined
  },
})

// Report file name
export const reportFileName = selector<string>({
  key: key('report-file-name'),
  get: ({ get }) => {
    const title = get(reportTitle)
    const place = get(googlePlace)
    const fileName = `${place?.name} - ${title.text}`
    return `${fileName}.pdf`
  },
})

// Used to convert map-based widgets to static images before downloading
export const isMapStatic = atom<boolean>({
  key: key('is-map-static'),
  default: false,
})

// Render Params for generating render config
const renderConfigParams = selector<PageRenderParams>({
  key: key('render-params'),
  get: ({ get }) => {
    const pageSize = get(reportPageSize)
    const pageDimensions = get(reportPageDimensions)
    const pageOrientation = get(reportOrientation)
    const pageColor = get(customReportPageColor)
    const header = get(reportHeader)
    const footer = get(customReportFooter)
    const images = get(visibleImages)
    const coverPage = get(customReportCoverPage)
    const logoDimensions = get(reportLogoDimensions)

    const config: PageRenderParams = {
      pageSize,
      pageDimensions,
      orientation: pageOrientation,
      pageColor,
      pageMargin: PDF_SPACING,
      coverPage,
      logoDimensions,
      pageHeader: header.visible ? header : undefined,
      pageFooter: footer.visible ? footer : undefined,
      images,
    }

    return config
  },
})

// Render Config used to generate Preview + PDF
export const renderConfig = selector<PagesRenderConfig>({
  key: key('render-config'),
  get: async ({ get }) => {
    const configParams = get(renderConfigParams)
    const config = await getRenderConfig(configParams)
    return config
  },
})

export const AVAILABLE_DOWNLOADS = 10
// Trial user limited downloads
export const reportDownloadLimit = atom<number>({
  key: key('report-download-limit'),
  default: 10,
  effects_UNSTABLE: [
    (params: any) => {
      const user = params.getLoadable(currentUser).contents
      if (!user || user.tier !== 'trial') {
        return
      }
      const key = `report-download-limit-${user.id}`
      return localStorageEffect(key)(params)
    },
  ],
})
export const csvDownloadLimit = atom<number>({
  key: key('csv-download-limit'),
  default: 10,
  effects_UNSTABLE: [
    (params: any) => {
      const user = params.getLoadable(currentUser).contents
      if (!user || user.tier !== 'trial') {
        return
      }
      const key = `csv-download-limit-${user.id}`
      return localStorageEffect(key)(params)
    },
  ],
})
